import { useState } from 'react';
import DataGridLayout from '../../components/layout/DataGridLayout';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Mail, Phone, Trash2, User, Briefcase } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import VerificationToggle from '../../components/ui/VerificationToggle';
import { EnquiryRequest } from '../../types/enquiry';

const MOCK_DRIVE_REQUESTS: EnquiryRequest[] = [
  {
    id: '1',
    emailId: 'sagarreddyguvvala.77@gmail.com',
    mobileNo: '9494744282',
    fullName: 'Sagar Guvvala',
    jobRole: 'Voice Process',
    isVerified: true
  },
  {
    id: '2',
    emailId: 'sagarreddyguvvala@gmail.com',
    mobileNo: '8919575710',
    fullName: 'Sagar Guvvala',
    jobRole: 'Voice Process',
    isVerified: true
  },
  {
    id: '3',
    emailId: 'rakesh@example.com',
    mobileNo: '8876543210',
    fullName: 'Rakesh Kumar',
    jobRole: 'Software Developer',
    isVerified: true
  },
  {
    id: '4',
    emailId: 'anjali@example.com',
    mobileNo: '9934567890',
    fullName: 'Anjali Sharma',
    jobRole: 'Data Analyst',
    isVerified: false
  },
  {
    id: '5',
    emailId: 'vikram@example.com',
    mobileNo: '7765432109',
    fullName: 'Vikram Singh',
    jobRole: 'UI Designer',
    isVerified: true
  }
];

const DriveRequests = () => {
  const [driveRequests, setDriveRequests] = useState<EnquiryRequest[]>(MOCK_DRIVE_REQUESTS);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleRefresh = () => {
    toast({
      title: "Refreshed",
      description: "Drive request data has been refreshed successfully.",
    });
  };

  const handleExport = () => {
    toast({
      title: "Export Started",
      description: "Your drive request data export has been initiated.",
    });
  };

  const handleToggleVerification = (id: string) => {
    setDriveRequests(prevRequests => 
      prevRequests.map(request => 
        request.id === id 
          ? { ...request, isVerified: !request.isVerified } 
          : request
      )
    );

    toast({
      title: "Verification Status Updated",
      description: "The verification status has been updated successfully.",
    });
  };

  const handleDelete = (id: string) => {
    setDriveRequests(prevRequests => prevRequests.filter(request => request.id !== id));
    
    toast({
      title: "Entry Deleted",
      description: "The drive request has been removed successfully.",
    });
  };

  const filteredRequests = driveRequests.filter(request => 
    request.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    request.emailId.toLowerCase().includes(searchQuery.toLowerCase()) ||
    request.mobileNo.includes(searchQuery) ||
    (request.jobRole && request.jobRole.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <DataGridLayout
      title="Drive Requests"
      subtitle="Manage placement drive requests and their verification status"
      searchPlaceholder="Search by name, email, phone or job role..."
      onSearch={handleSearch}
      onRefresh={handleRefresh}
      onExport={handleExport}
    >
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[250px]">Email ID</TableHead>
              <TableHead>Mobile No</TableHead>
              <TableHead>Full Name</TableHead>
              <TableHead>Job Role</TableHead>
              <TableHead className="text-center">Is Verified</TableHead>
              <TableHead className="text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRequests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                  No drive requests found
                </TableCell>
              </TableRow>
            ) : (
              filteredRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Mail size={16} className="text-blue-500" />
                      {request.emailId}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-green-500" />
                      {request.mobileNo}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User size={16} className="text-purple-500" />
                      {request.fullName}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Briefcase size={16} className="text-amber-500" />
                      {request.jobRole}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <VerificationToggle 
                      isVerified={request.isVerified} 
                      onToggle={() => handleToggleVerification(request.id)}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(request.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={18} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </DataGridLayout>
  );
};

export default DriveRequests;
